/**
 * 最近浏览页面 —— 展示最近访问的条目。
 */

import { Link } from 'react-router-dom';
import { EntryIcon } from '@/components/EntryIcon';
import type { ContentRepository } from '@/core/contentRepository';
import { useLibraryStore } from '@/store/useLibraryStore';
import { topic } from '@/core/moduleCatalog';
import { ArrowLeft, History } from 'lucide-react';

interface RecentPageProps {
  repository: ContentRepository;
}

export function RecentPage({ repository }: RecentPageProps) {
  const recentVisits = useLibraryStore((s) => s.recentVisits);
  const clearHistory = useLibraryStore((s) => s.clearHistory);

  const entries = recentVisits
    .map((v) => repository.entry(v.entryID))
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-400 transition-colors hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">最近浏览</h1>
        </div>
        {entries.length > 0 && (
          <button
            onClick={clearHistory}
            className="text-xs text-slate-400 hover:text-red-500"
          >
            清除全部
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <History size={48} className="mb-4 opacity-50" />
          <p className="text-sm">还没有浏览记录</p>
        </div>
      ) : (
        <div className="space-y-1">
          {entries.map((entry) => {
            const t = topic(entry!.topicID);
            return (
              <Link
                key={entry!.id}
                to={`/topic/${entry!.topicID}#${entry!.id}`}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <EntryIcon entry={entry!} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {entry!.nameZh}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {t?.title ?? entry!.topicID}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
